import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Film, Image as ImageIcon, Languages, Loader2, Mic2, Pencil, RefreshCw, Save, Video, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { fetchVideoGenerationPrompt } from '@/lib/videoGenerationPrompt';
import { VideoGenerationPromptDialog } from '@/components/VideoGenerationPromptDialog';
import { CreateAgentBackButton } from '@/components/createPlan/CreateAgentBackButton';
import { agentCreatePath } from '@/lib/creationPlan';
import {
  finalizeNarrationSegmentAudioRegeneration,
  isNarrationSegmentAudioResult,
} from '@/lib/narrationSegmentAudioRegen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const NARRATION_LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'kn', label: 'Kannada' },
  { value: 'mr', label: 'Marathi' },
  { value: 'ta', label: 'Tamil' },
];

const mediaUrlForPlayback = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}/${url}`;
};

export function NarrationSegmentsEditor({ variant = 'listing' }) {
  const isGridEditor = variant === 'grid';
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [editing, setEditing] = useState({});
  const [langPick, setLangPick] = useState({});
  const [wholeLangPick, setWholeLangPick] = useState('');
  const [wholeScriptBusy, setWholeScriptBusy] = useState(false);
  const [wholeScriptRegenerateAudio, setWholeScriptRegenerateAudio] = useState(false);
  const [videoPromptReview, setVideoPromptReview] = useState(null);
  const [videoPromptReviewLoading, setVideoPromptReviewLoading] = useState(false);

  const loadProject = async () => {
    try {
      const { data } = await api.get(`/narration/projects/${projectId}`);
      setProject(data);
    } catch (err) {
      toast.error('Could not load narration scenes');
      navigate(agentCreatePath({ flowId: 'narration' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const runSegmentAction = async (segmentId, action, label, editedPrompt) => {
    const key = `${segmentId}:${action}`;
    setBusy((prev) => ({ ...prev, [key]: true }));
    try {
      const body = (action === 'generate-video' || action === 'regenerate-image')
        ? { async_job: true, ...(editedPrompt ? { prompt: editedPrompt } : {}) }
        : undefined;
      const { data } = await api.post(`/narration/projects/${projectId}/segments/${segmentId}/${action}`, body);
      if (data?.job_id) {
        const jobId = data.job_id;
        await new Promise((resolve, reject) => {
          const started = Date.now();
          const interval = setInterval(async () => {
            try {
              const { data: job } = await api.get(`/jobs/${jobId}`);
              if (job.status === 'completed') {
                clearInterval(interval);
                resolve(null);
              } else if (job.status === 'failed' || job.status === 'retry_scheduled') {
                clearInterval(interval);
                reject(new Error(job.error_message || `${label} failed`));
              } else if (Date.now() - started > 10 * 60 * 1000) {
                clearInterval(interval);
                reject(new Error(`${label} timed out`));
              }
            } catch (pollErr) {
              /* keep polling through transient errors */
              if (Date.now() - started > 10 * 60 * 1000) {
                clearInterval(interval);
                reject(pollErr);
              }
            }
          }, 2500);
        });
      }
      if (action === 'regenerate-audio' && isNarrationSegmentAudioResult(data)) {
        try {
          await finalizeNarrationSegmentAudioRegeneration(projectId, data, { silent: true });
        } catch {
          /* segment audio saved; cloud version append is best-effort */
        }
      }
      await loadProject();
      toast.success(`${label} complete`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || `${label} failed`);
    } finally {
      setBusy((prev) => ({ ...prev, [key]: false }));
    }
  };

  const openSegmentVideoPromptReview = async (segmentId, label) => {
    const provider = project?.input?.video_provider || 'narration_slideshow';
    setVideoPromptReview({ segmentId, label, provider, prompt: '', maxLength: 2000 });
    setVideoPromptReviewLoading(true);
    try {
      const data = await fetchVideoGenerationPrompt({
        projectId,
        segmentId,
        provider,
      });
      setVideoPromptReview((prev) => ({
        ...prev,
        prompt: data.prompt || '',
        maxLength: data.max_length || 2000,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load video prompt');
      setVideoPromptReview(null);
    } finally {
      setVideoPromptReviewLoading(false);
    }
  };

  const confirmSegmentVideoPrompt = async (editedPrompt) => {
    if (!videoPromptReview) return;
    const { segmentId, label } = videoPromptReview;
    setVideoPromptReview(null);
    await runSegmentAction(segmentId, 'generate-video', label, editedPrompt);
  };

  const switchWholeScriptLanguage = async () => {
    const language = wholeLangPick;
    if (!language) {
      toast.error('Choose a target language for the whole story');
      return;
    }
    const cur = String(project?.input?.language || 'en').toLowerCase();
    if (language === cur) {
      toast.error('Project is already set to this language');
      return;
    }
    setWholeScriptBusy(true);
    try {
      await api.post(`/narration/projects/${projectId}/switch-script-language`, {
        language,
        regenerate_audio: wholeScriptRegenerateAudio,
        persist: true,
      });
      await loadProject();
      toast.success(
        wholeScriptRegenerateAudio
          ? 'Whole script translated with new narration audio. Earlier scripts and media are in history.'
          : 'Whole script translated. Regenerate audio per scene or from the editor when ready. Earlier scripts and media are in history.',
      );
      setWholeLangPick('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not translate the whole script');
    } finally {
      setWholeScriptBusy(false);
    }
  };

  const switchSegmentLanguage = async (segmentId) => {
    const language = langPick[segmentId];
    if (!language) {
      toast.error('Choose a target language first');
      return;
    }
    const key = `${segmentId}:switch-language`;
    setBusy((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await api.post(`/narration/projects/${projectId}/segments/${segmentId}/switch-language`, {
        language,
        regenerate_audio: true,
      });
      if (isNarrationSegmentAudioResult(data)) {
        try {
          await finalizeNarrationSegmentAudioRegeneration(projectId, data, { silent: true });
        } catch {
          /* non-fatal */
        }
      }
      await loadProject();
      toast.success('Script translated and new narration audio generated. Earlier versions are in history.');
      setLangPick((prev) => {
        const next = { ...prev };
        delete next[segmentId];
        return next;
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch segment language');
    } finally {
      setBusy((prev) => ({ ...prev, [key]: false }));
    }
  };

  const startEditing = (segment) => {
    setEditing((prev) => ({
      ...prev,
      [segment.id]: {
        text: segment.text || '',
        visual: segment.visual || '',
        emotion: segment.emotion || '',
        phoneme_notes: segment.phoneme_notes || '',
      },
    }));
  };

  const updateEditing = (segmentId, key, value) => {
    setEditing((prev) => ({
      ...prev,
      [segmentId]: {
        ...(prev[segmentId] || {}),
        [key]: value,
      },
    }));
  };

  const cancelEditing = (segmentId) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[segmentId];
      return next;
    });
  };

  const saveScript = async (segmentId) => {
    const draft = editing[segmentId];
    if (!draft?.text?.trim()) {
      toast.error('Scene script cannot be empty');
      return;
    }
    const key = `${segmentId}:save-script`;
    setBusy((prev) => ({ ...prev, [key]: true }));
    try {
      await api.patch(`/narration/projects/${projectId}/segments/${segmentId}`, draft);
      cancelEditing(segmentId);
      await loadProject();
      toast.success('Scene script updated. Regenerate audio and video for this scene.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update scene script');
    } finally {
      setBusy((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  const segments = project?.segments || [];
  const withImages = segments.filter((s) => s.image_url).length;
  const withAudio = segments.filter((s) => s.audio_url).length;
  const withVideos = segments.filter((s) => s.video_url).length;

  const statsChips = (
    <>
      <span
        className="rounded-full px-3 py-1 text-xs font-semibold"
        style={
          isGridEditor
            ? { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }
            : { background: 'white', color: '#64748B', border: '1px solid rgb(241 245 249)' }
        }
      >
        Images {withImages}/{segments.length}
      </span>
      <span
        className="rounded-full px-3 py-1 text-xs font-semibold"
        style={
          isGridEditor
            ? { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }
            : { background: 'white', color: '#64748B', border: '1px solid rgb(241 245 249)' }
        }
      >
        Audio {withAudio}/{segments.length}
      </span>
      <span
        className="rounded-full px-3 py-1 text-xs font-semibold"
        style={
          isGridEditor
            ? { background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--glass-border)' }
            : { background: 'white', color: '#64748B', border: '1px solid rgb(241 245 249)' }
        }
      >
        Video {withVideos}/{segments.length}
      </span>
    </>
  );

  return (
    <div
      className={isGridEditor ? 'min-h-screen pb-20' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20'}
      style={isGridEditor ? { background: 'var(--bg-primary)' } : undefined}
      data-testid={isGridEditor ? 'narration-scene-grid-editor' : 'narration-scene-listing-page'}
    >
      {isGridEditor ? (
        <div className="border-b shrink-0" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-4 min-w-0 flex-wrap">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/narration/${projectId}/video-editor`)}
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid="narration-edit-back-to-video-editor"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Video editor
                </Button>
                <div className="flex items-center gap-2 text-sm flex-wrap">
                  <span style={{ color: 'var(--text-secondary)' }}>Narration</span>
                  <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>Edit scenes</span>
                  <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                  <span style={{ color: 'var(--text-secondary)' }}>Editor</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {statsChips}
                <CreateAgentBackButton
                  projectId={projectId}
                  className="rounded-lg gap-2"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                />
                <Button
                  onClick={() => navigate(`/narration/${projectId}/video-editor`)}
                  variant="outline"
                  className="rounded-lg gap-2"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
                  data-testid="open-narration-editor-from-edit-btn"
                >
                  <Film className="w-4 h-4" /> Edit video
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(agentCreatePath({ flowId: 'narration' }))}
            className="rounded-xl gap-2 mb-3 text-[#64748B]"
          >
            <ArrowLeft className="w-4 h-4" /> Narration
          </Button>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#1E293B]">
            Narration Scenes
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Review queued images, generated audio, and segment videos before opening the timeline editor.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {statsChips}
          <CreateAgentBackButton projectId={projectId} className="rounded-xl gap-2 text-[#1E293B]" />
          <Button
            type="button"
            onClick={() => navigate(`/narration/${projectId}/video-editor`)}
            className="rounded-xl gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white"
            data-testid="open-narration-editor-from-scenes-btn"
          >
            <Film className="w-4 h-4" /> Open in Editor
          </Button>
        </div>
      </div>
      )}

      <div className={isGridEditor ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : undefined}>
      {isGridEditor ? (
        <div className="py-6">
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {project?.title || project?.input?.topic || 'Narration project'}
          </h1>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
            Edit segment scripts, regenerate images and audio, and generate videos — same workflow as story scene editing.
          </p>
        </div>
      ) : null}

      {segments.length > 0 ? (
        <div className="rounded-3xl bg-gradient-to-br from-indigo-50/90 to-white border border-indigo-100 p-5 mb-6 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div className="space-y-1">
              <p className="text-xs font-bold uppercase tracking-wide text-[#6366F1]">Whole story language</p>
              <p className="text-sm text-[#64748B]">
                Translate every segment together while preserving storytelling tone and emotion. Older scripts (with optional audio snapshots)
                appear under each segment in “Previous script versions”; replaced narration videos move to media history.
              </p>
              <p className="text-xs text-[#94A3B8]">
                Hindi, Marathi, Tamil, Kannada, or English — regenerated audio follows project voice settings and the new language.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={wholeLangPick || ''} onValueChange={setWholeLangPick}>
                <SelectTrigger className="h-9 w-[168px] rounded-xl text-xs" data-testid="whole-script-lang-select">
                  <SelectValue placeholder="Target language" />
                </SelectTrigger>
                <SelectContent>
                  {NARRATION_LANG_OPTIONS.filter(
                    (o) => o.value !== String(project?.input?.language || 'en').toLowerCase(),
                  ).map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={wholeScriptRegenerateAudio}
                  onChange={(e) => setWholeScriptRegenerateAudio(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="whole-script-regenerate-audio"
                />
                Regenerate all narration audio
              </label>
              <Button
                type="button"
                disabled={wholeScriptBusy || !wholeLangPick}
                onClick={switchWholeScriptLanguage}
                className="rounded-xl gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white"
                data-testid="whole-script-switch-lang-btn"
              >
                {wholeScriptBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
                Translate whole script
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {segments.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 p-8 text-center text-[#64748B]">
          No narration segments are ready yet. Generate a narration project first.
        </div>
      ) : (
        <div className={`grid gap-5 ${isGridEditor ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          {segments.map((segment) => {
            const imageBusy = busy[`${segment.id}:regenerate-image`];
            const audioBusy = busy[`${segment.id}:regenerate-audio`];
            const langBusy = busy[`${segment.id}:switch-language`];
            const videoBusy = busy[`${segment.id}:generate-video`];
            const saveBusy = busy[`${segment.id}:save-script`];
            const draft = editing[segment.id];
            return (
              <div key={segment.id} className="rounded-3xl bg-white border border-slate-100 shadow-[0_16px_50px_-24px_rgba(15,23,42,0.22)] overflow-hidden">
                <div className="aspect-video bg-slate-100 flex items-center justify-center">
                  {segment.video_url ? (
                    <video src={mediaUrlForPlayback(segment.video_url)} controls className="w-full h-full object-contain bg-black" />
                  ) : segment.image_url ? (
                    <img src={mediaUrlForPlayback(segment.image_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-10 h-10 text-[#94A3B8]" />
                  )}
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-[#6366F1]">
                        Segment {segment.segment_number}
                      </p>
                      <p className="text-xs text-[#94A3B8] mt-1">
                        {Number(segment.audio_duration || segment.duration || 0).toFixed(2)}s audio
                        {segment.subtitles_burned ? ' · subtitles' : ''}
                        {' · '}
                        <span className="font-semibold text-[#64748B]">
                          Script:{' '}
                          {NARRATION_LANG_OPTIONS.find(
                            (o) =>
                              o.value
                              === String(segment.segment_language || project?.input?.language || 'en').toLowerCase(),
                          )?.label || String(segment.segment_language || project?.input?.language || 'en')}
                        </span>
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-50 border border-slate-100 px-2 py-1 text-[10px] font-semibold text-[#64748B]">
                      {segment.emotion || 'narration'}
                    </span>
                  </div>
                  {draft ? (
                    <div className="space-y-3 rounded-2xl border border-[#6366F1]/20 bg-[#EEF2FF]/30 p-3">
                      <div>
                        <p className="text-xs font-bold text-[#1E293B] mb-1">Scene Script</p>
                        <Textarea
                          value={draft.text}
                          onChange={(event) => updateEditing(segment.id, 'text', event.target.value)}
                          className="min-h-28 rounded-xl border-slate-200 bg-white"
                          data-testid={`edit-narration-scene-script-${segment.id}`}
                        />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#1E293B] mb-1">Visual Prompt</p>
                        <Textarea
                          value={draft.visual}
                          onChange={(event) => updateEditing(segment.id, 'visual', event.target.value)}
                          className="min-h-20 rounded-xl border-slate-200 bg-white"
                          data-testid={`edit-narration-scene-visual-${segment.id}`}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={saveBusy}
                          onClick={() => saveScript(segment.id)}
                          className="rounded-xl gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white"
                        >
                          {saveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Save script
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => cancelEditing(segment.id)}
                          className="rounded-xl gap-2"
                        >
                          <X className="w-3.5 h-3.5" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm leading-relaxed text-[#334155]">{segment.text}</p>
                      {segment.visual && (
                        <p className="text-xs leading-relaxed text-[#64748B]">
                          <span className="font-semibold">Visual:</span> {segment.visual}
                        </p>
                      )}
                      {(segment.script_history || []).length > 0 ? (
                        <details className="rounded-xl border border-slate-100 bg-slate-50/90 p-3 text-xs">
                          <summary className="cursor-pointer font-semibold text-[#475569]">
                            Previous script versions ({segment.script_history.length})
                          </summary>
                          <ul className="mt-2 space-y-3 max-h-44 overflow-y-auto pr-1">
                            {[...(segment.script_history || [])].reverse().map((h, idx) => (
                              <li
                                key={`${h.created_at || idx}-${idx}`}
                                className="border-b border-slate-100 pb-2 last:border-0"
                              >
                                <p className="text-[10px] font-semibold text-[#6366F1]">
                                  {NARRATION_LANG_OPTIONS.find((o) => o.value === (h.language || '').toLowerCase())?.label
                                    || h.language}{' '}
                                  · {h.created_at ? new Date(h.created_at).toLocaleString() : ''}
                                </p>
                                <p className="text-[#334155] whitespace-pre-wrap mt-1">{h.text}</p>
                                {h.visual ? (
                                  <p className="text-[11px] text-[#64748B] mt-1 whitespace-pre-wrap">
                                    <span className="font-semibold">Visual:</span> {h.visual}
                                  </p>
                                ) : null}
                                {h.emotion ? (
                                  <p className="text-[10px] text-[#94A3B8] mt-1">Tone: {h.emotion}</p>
                                ) : null}
                                {h.audio_url ? (
                                  <audio
                                    controls
                                    src={mediaUrlForPlayback(h.audio_url)}
                                    className="w-full h-8 mt-2"
                                  />
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : null}
                    </div>
                  )}
                  {segment.audio_url && (
                    <audio controls src={mediaUrlForPlayback(segment.audio_url)} className="w-full h-10" />
                  )}
                  <div className="flex flex-wrap items-center gap-2 py-2 border-t border-slate-100">
                    <span className="text-[11px] font-semibold text-[#64748B] shrink-0">Switch language</span>
                    <Select
                      value={langPick[segment.id] ?? ''}
                      onValueChange={(v) => setLangPick((prev) => ({ ...prev, [segment.id]: v }))}
                    >
                      <SelectTrigger className="h-8 w-[140px] rounded-xl text-xs" data-testid={`segment-lang-select-${segment.id}`}>
                        <SelectValue placeholder="Target language" />
                      </SelectTrigger>
                      <SelectContent>
                        {NARRATION_LANG_OPTIONS.filter((o) => {
                          const cur = String(
                            segment.segment_language || project?.input?.language || 'en',
                          ).toLowerCase();
                          return o.value !== cur;
                        }).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-xl gap-2"
                      disabled={langBusy || !langPick[segment.id]}
                      onClick={() => switchSegmentLanguage(segment.id)}
                      data-testid={`segment-switch-lang-${segment.id}`}
                      title="Translates this segment while preserving emotional tone; saves the prior script (with optional audio snapshot) and archives replaced narration/video to history."
                    >
                      {langBusy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Languages className="w-3.5 h-3.5" />
                      )}
                      Translate &amp; new audio
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!draft && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(segment)}
                        className="rounded-xl gap-2"
                        data-testid={`edit-narration-scene-script-btn-${segment.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit script
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={imageBusy}
                      onClick={() => runSegmentAction(segment.id, 'regenerate-image', 'Image regeneration')}
                      className="rounded-xl gap-2"
                    >
                      {imageBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                      Regenerate image
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={audioBusy}
                      onClick={() => runSegmentAction(segment.id, 'regenerate-audio', 'Audio regeneration')}
                      className="rounded-xl gap-2"
                    >
                      {audioBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic2 className="w-3.5 h-3.5" />}
                      Regenerate audio
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={videoBusy || !segment.image_url || !segment.audio_url}
                      onClick={() => openSegmentVideoPromptReview(segment.id, 'Segment video generation')}
                      className="rounded-xl gap-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white"
                    >
                      {videoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
                      {segment.video_url ? 'Regenerate video' : 'Generate video'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={loadProject}
                      className="rounded-xl gap-2 text-[#64748B]"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Refresh
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      <VideoGenerationPromptDialog
        open={Boolean(videoPromptReview)}
        onOpenChange={(open) => { if (!open) setVideoPromptReview(null); }}
        provider={videoPromptReview?.provider}
        initialPrompt={videoPromptReview?.prompt || ''}
        maxLength={videoPromptReview?.maxLength || 2000}
        loading={videoPromptReviewLoading}
        onConfirm={confirmSegmentVideoPrompt}
      />
    </div>
  );
}

export const NarrationSceneListingPage = () => <NarrationSegmentsEditor variant="listing" />;

export default NarrationSceneListingPage;
